import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Response, ThemeItem, ThemeConfig } from '../../types';
import { getNotificationInstance } from '../utils/notification';

const baseQuery = fetchBaseQuery({ baseUrl: '/api/user' });

// @ts-ignore
const baseQueryWithErrorHandling = async (args, api, extraOptions) => {
    const notification = getNotificationInstance();

    const result = await baseQuery(args, api, extraOptions);
    if (result.error) {
        if (notification) {
            notification.error({
                message: 'Ошибка',
                description: 'Произошла ошибка при выполнении запроса',
                placement: 'topRight',
            });
        }
        else {
            alert('Произошла ошибка при выполнении запроса');
        }
    }
    return result;
};


export const userSlice = createApi({
    reducerPath: 'userApi',

    baseQuery: baseQueryWithErrorHandling,

    tagTypes: ['Themes', 'PublicThemes'],

    endpoints: (builder) => ({
        getThemes: builder.query<Response<ThemeItem[]>, void>({
            query: () => '/get-themes',
            providesTags: ['Themes'],
        }),

        getPublicThemes: builder.query<Response<ThemeItem[]>, void>({
            query: () => '/get-public-themes',
            providesTags: ['PublicThemes'],
        }),

        createTheme: builder.mutation<Response, {
            name: string,
            theme: ThemeConfig,
        }>({
            query: (request) => ({
                url: '/create-theme',
                method: 'POST',
                body: request,
            }),
            invalidatesTags: ['Themes'],
        }),

        deleteTheme: builder.mutation<Response, {
            themeId: number,
        }>({
            query: (request) => ({
                url: '/delete-theme',
                method: 'POST',
                body: request,
            }),
            invalidatesTags: ['Themes', 'PublicThemes'],
        }),

        updateTheme: builder.mutation<Response, {
            name: string,
            theme: ThemeConfig,
            themeId: number,
        }>({
            query: (request) => ({
                url: '/update-theme',
                method: 'POST',
                body: request,
            }),
            invalidatesTags: ['Themes'],
        }),

        updatePublicTheme: builder.mutation<Response, {
            themeId: number,
            value: boolean,
        }>({
            query: (request) => ({
                url: '/update-public-theme',
                method: 'POST',
                body: request,
            }),
            invalidatesTags: ['Themes', 'PublicThemes'],
        }),

        copyTheme: builder.mutation<Response, {
            themeId: number,
        }>({
            query: (request) => ({
                url: '/copy-theme',
                method: 'POST',
                body: request,
            }),
            invalidatesTags: ['Themes'],
        }),

        uploadThemeBackground: builder.mutation<Response<{ imagePath: string }>, File>({
            query: (file) => {
                const formData = new FormData();
                formData.append('file', file);
                return {
                    url: '/upload-theme-background',
                    method: 'POST',
                    body: formData,
                };
            },
        }),
    }),
});

export const {
    useGetThemesQuery,
    useGetPublicThemesQuery,
    useCreateThemeMutation,
    useDeleteThemeMutation,
    useUpdateThemeMutation,
    useUpdatePublicThemeMutation,
    useCopyThemeMutation,
    useUploadThemeBackgroundMutation,
} = userSlice;
